import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { postNamedCommand, postNamedQuery } from '../service'

const getThreadQueryKey = (agentId, threadId) => ['agents/thread', agentId, threadId]
const getLatestThreadQueryKey = (agentId) => ['agents/latestThread', agentId]
const isThreadInProgress = (status) => status === 'pending' || status === 'running' || status === 'pending_action'

export const agents = {
  useInvokeAgent: (agentId) => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async ({ message, threadId, attachments }) => {
        const { data } = await postNamedCommand('agents', `invoke-${agentId}`, {
          message,
          threadId,
          attachments
        })
        return data
      },
      onSuccess: (data) => {
        // Invalidate thread query to trigger refetch
        if (data.threadId) {
          queryClient.invalidateQueries({ queryKey: getThreadQueryKey(agentId, data.threadId) })
        }
      }
    })
  },

  useAgentThread: (threadId, options = {}) => {
    const { enabled = true, agentId } = options
    return useQuery({
      queryKey: getThreadQueryKey(agentId, threadId),
      enabled: enabled && !!threadId,
      queryFn: async () => {
        const { data } = await postNamedQuery('agents', 'get-agent-thread', {
          threadId,
          detail: 'full'
        })
        return data
      },
      refetchInterval: (data) => {
        return isThreadInProgress(data?.status) ? 1500 : false
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: false
    })
  },

  useResumeAgent: (agentId) => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async ({ threadId, action, feedback }) => {
        const { data } = await postNamedCommand('agents', `resume-${agentId}`, {
          threadId,
          action,
          feedback
        })
        return data
      },
      onSuccess: (data) => {
        if (data.threadId) {
          queryClient.invalidateQueries({ queryKey: getThreadQueryKey(agentId, data.threadId) })
        }
      }
    })
  },

  useCancelAgent: (agentId) => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async ({ threadId }) => {
        const { data } = await postNamedCommand('agents', `cancel-${agentId}`, { threadId })
        return data
      },
      onSuccess: (data) => {
        if (data.threadId) {
          queryClient.invalidateQueries({ queryKey: getThreadQueryKey(agentId, data.threadId) })
        }
      }
    })
  },

  useEndAgent: (agentId) => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async ({ threadId }) => {
        const { data } = await postNamedCommand('agents', `end-${agentId}`, { threadId })
        return data
      },
      onSuccess: (data) => {
        if (data?.threadId) {
          queryClient.invalidateQueries({ queryKey: getThreadQueryKey(agentId, data.threadId) })
        }
        queryClient.invalidateQueries({ queryKey: getLatestThreadQueryKey(agentId) })
      }
    })
  },

  useLatestAgentThread: (agentId, options = {}) => {
    const { enabled = true } = options
    return useQuery({
      queryKey: getLatestThreadQueryKey(agentId),
      enabled: enabled && !!agentId,
      queryFn: async () => {
        const { data } = await postNamedQuery('agents', 'get-latest-agent-thread', { agentId })
        return data // { threadId: string | null, status?: string, modifiedAt?: string }
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: 30_000
    })
  }
}
